// =================================================================
// BasesSubmenu.js — S45 P1
// Sous-menu "Dernières bases créées" dans la sidebar (sous Bases d'Expertise)
// Liste les N bases les plus récentes (admin_general / oracle_fondateur)
// =================================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Database, ChevronRight } from 'lucide-react';

const REFRESH_INTERVAL_MS = 60_000;
const LS_COLLAPSED = 'itriia_bases_submenu_collapsed_v1';

// Icônes par catégorie/template (mini-taxonomie)
const TEMPLATE_EMOJI = {
  general: '📚',
  scientific: '🔬',
  scientific_heavy: '🔬',
  book: '📖',
  paper: '📄',
  manifesto: '📜',
  methodology: '🧭',
  methodo: '🧭',
  corpus: '🧩',
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'à l\'instant';
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `il y a ${d} j`;
  const w = Math.floor(d / 7);
  if (w < 5) return `il y a ${w} sem.`;
  return new Date(iso).toLocaleDateString('fr-FR');
};

const BasesSubmenu = ({ onOpen, limit = 5 }) => {
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_COLLAPSED) === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_COLLAPSED, next ? '1' : '0'); } catch { /* silent */ }
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      const resp = await axios.get('/api/expertise-bases/recent', { params: { limit } });
      setBases(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      console.warn('bases/recent load failed:', e?.response?.status || e.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="bases-submenu">
      <style>{`
        .bases-submenu {
          margin: 4px 8px 8px 16px;
          padding: 6px 0 4px;
          border-left: 2px solid var(--card-border);
          background: var(--sub-items-bg);
          border-radius: 8px;
        }
        .bs-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: #3b82f6;
          padding: 8px 10px 6px;
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          border-radius: 6px;
          transition: background 0.15s ease;
        }
        .bs-header:hover { background: var(--sidebar-active-bg); }
        .bs-header-chevron {
          flex-shrink: 0;
          opacity: 0.75;
          transition: transform 0.15s ease;
        }
        .bs-header-chevron.open { transform: rotate(90deg); }
        .bs-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          margin: 1px 6px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12.5px;
          color: var(--sidebar-text);
          transition: background 0.15s ease, color 0.15s ease;
        }
        .bs-item:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .bs-icon {
          flex-shrink: 0;
          opacity: 0.9;
          font-size: 14px;
          width: 16px;
          text-align: center;
        }
        .bs-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .bs-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 500;
        }
        .bs-meta {
          font-size: 10.5px;
          color: var(--text-muted, #6a6a85);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bs-empty {
          padding: 8px 12px;
          font-size: 11.5px;
          color: var(--text-muted, #6a6a85);
          font-style: italic;
        }
      `}</style>

      <button
        type="button"
        className="bs-header"
        onClick={toggleCollapsed}
        title={collapsed ? 'Afficher Dernières bases' : 'Masquer'}
      >
        <ChevronRight size={11} className={`bs-header-chevron ${!collapsed ? 'open' : ''}`} />
        <span>Dernières bases créées</span>
      </button>

      {!collapsed && loading && bases.length === 0 && (
        <div className="bs-empty">Chargement…</div>
      )}
      {!collapsed && !loading && bases.length === 0 && (
        <div className="bs-empty">Aucune base encore</div>
      )}

      {!collapsed && bases.map((b) => {
        const key = b.template_key || b.category || 'general';
        const emoji = TEMPLATE_EMOJI[key] || <Database size={12} />;
        return (
          <div
            key={b.id}
            className="bs-item"
            onClick={() => onOpen?.(b)}
            title={`${b.name} · ${b.template_name || key}`}
          >
            <div className="bs-icon">
              {typeof emoji === 'string' ? emoji : emoji}
            </div>
            <div className="bs-main">
              <div className="bs-title">{b.name}</div>
              <div className="bs-meta">
                {(b.template_name || key) + ' · ' + relativeTime(b.created_at)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BasesSubmenu;
